import { useState, type ReactNode } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Box, Button, Checkbox, Flex, Grid, Select, Text } from '@radix-ui/themes'
import { FieldShell } from '~/components/FieldShell'
import { useTRPC } from '~/utils/trpc'
import { Ops } from '@willie/schemas'
import { loggerMakerLabel, meterCompatibilityLabel } from '../constants'
import { profileOptions } from '../profileSuggestions'
import { useInvalidateCompatibility } from '../useInvalidateCompatibility'
import { MeterMakerPicker } from './MeterMakerPicker'
import { MeterModelPicker } from './MeterModelPicker'

const NO_PROFILE = '__none__'

export interface CompatibilityDraft {
  meterMaker: string | null
  meterModel: string | null
  installationProfile: string | null
  notCompatible: boolean
}

interface CompatibilityVerdictFormProps {
  installationId: number
  initial: CompatibilityDraft
  // A review settles the meter, so it may not save as in analysis.
  requireDecision?: boolean
  submitLabel: string
  onSaved?: () => void
}

const toVerdict = (draft: CompatibilityDraft): Ops.StationInstallations.CompatibilityVerdict => {
  if (draft.notCompatible) return { outcome: 'not_compatible' }
  if (draft.installationProfile) return { outcome: 'compatible', installationProfile: draft.installationProfile }
  return { outcome: 'in_analysis' }
}

export const CompatibilityVerdictForm = ({
  installationId,
  initial,
  requireDecision = false,
  submitLabel,
  onSaved
}: CompatibilityVerdictFormProps) => {
  const trpc = useTRPC()
  const invalidate = useInvalidateCompatibility()
  const profilesQuery = useQuery(
    trpc.ops.installationProfiles.list.queryOptions(undefined, { staleTime: Infinity })
  )
  const [draft, setDraft] = useState(initial)

  const save = useMutation(
    trpc.ops.stationInstallations.setCompatibility.mutationOptions({
      onSuccess: async () => {
        await invalidate()
        onSaved?.()
      }
    })
  )

  const catalog = profilesQuery.data ?? []
  const catalogBySlug = new Map(catalog.map((profile) => [profile.slug, profile]))
  const options = profileOptions(
    catalog,
    { meterMaker: draft.meterMaker, meterModel: draft.meterModel },
    draft.installationProfile
  )

  const verdict = toVerdict(draft)
  const profile = verdict.outcome === 'compatible' ? verdict.installationProfile : null
  const undecided = requireDecision && verdict.outcome === 'in_analysis'

  const profileHint = (): ReactNode => {
    if (profilesQuery.error) {
      return (
        <Text size="1" color="red">
          Could not load the installation profiles.
        </Text>
      )
    }
    if (profile) {
      const catalogRow = catalogBySlug.get(profile)
      if (!catalogRow) return null
      const loggerMaker = Ops.InstallationProfileCatalog.profileLoggerMaker(catalogRow.logger_makers)
      return (
        <Flex direction="column" gap="1">
          <Text size="1" color={catalogRow.supported ? 'green' : 'orange'}>
            {catalogRow.supported
              ? 'Compatible and supported: the order can be placed.'
              : 'Compatible but not supported: the hardware team must approve it before ordering.'}
          </Text>
          <Text size="1" color="gray">
            {loggerMaker
              ? `Logger: ${loggerMakerLabel[loggerMaker]}`
              : 'This profile has no logger maker; the installation keeps its current one.'}
          </Text>
        </Flex>
      )
    }
    if (draft.notCompatible) return null
    return (
      <Text size="1" color="gray">
        {undecided
          ? 'Pick a profile, or mark the meter not compatible.'
          : 'Without a profile the meter stays in analysis.'}
      </Text>
    )
  }

  const submit = () =>
    save.mutate({
      installationId,
      verdict,
      meterMaker: draft.meterMaker,
      meterModel: draft.meterModel
    })

  return (
    <Flex direction="column" gap="3">
      <Grid columns={{ initial: '1', sm: '2' }} gap="3">
        <MeterMakerPicker
          value={draft.meterMaker}
          onChange={(meterMaker) => setDraft({ ...draft, meterMaker, meterModel: null })}
        />
        <MeterModelPicker
          meterMaker={draft.meterMaker}
          value={draft.meterModel}
          onChange={(meterModel) => setDraft({ ...draft, meterModel })}
        />
        <Box gridColumn={{ initial: '1', sm: 'span 2' }}>
          <FieldShell label="Installation profile" hint={profileHint()}>
            <Select.Root
              disabled={draft.notCompatible}
              value={draft.notCompatible ? NO_PROFILE : (draft.installationProfile ?? NO_PROFILE)}
              onValueChange={(value) =>
                setDraft({ ...draft, installationProfile: value === NO_PROFILE ? null : value })
              }
            >
              <Select.Trigger className="w-full" />
              <Select.Content>
                <Select.Item value={NO_PROFILE}>—</Select.Item>
                {options.map((slug) => (
                  <Select.Item key={slug} value={slug}>
                    {catalogBySlug.get(slug)?.supported === false ? `${slug} (not supported)` : slug}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </FieldShell>
        </Box>
      </Grid>
      <Text as="label" size="2">
        <Flex align="center" gap="2">
          <Checkbox
            color="red"
            checked={draft.notCompatible}
            onCheckedChange={(checked) => setDraft({ ...draft, notCompatible: checked === true })}
          />
          Not compatible: no module fits this meter
        </Flex>
      </Text>
      <Flex align="center" gap="3">
        <Button disabled={undecided || save.isPending} loading={save.isPending} onClick={submit}>
          {submitLabel}
        </Button>
        {undecided ? null : (
          <Text size="1" color="gray">
            Saves as {meterCompatibilityLabel[verdict.outcome].toLowerCase()}.
          </Text>
        )}
        {save.error ? (
          <Text size="1" color="red">
            {save.error.message}
          </Text>
        ) : null}
      </Flex>
    </Flex>
  )
}
